import Page from '@components/Page/Page'
import { BooleanTag } from '@components/utils/BooleanTag'
import {
  SevenroomsAudience as SevenroomsAudienceModel,
  SevenroomsVenueGroup,
} from '@models/venue/integrations/SevenroomsAudience'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getSevenroomsAudiences,
  selectSevenroomsAudiences,
} from '@store/venue/integrations/SevenroomsAudienceReducer'
import {
  getSevenroomsVenueGroups,
  selectSevenroomsVenueGroups,
} from '@store/venue/integrations/SevenroomsVenueGroupReducer'
import {
  EllipsisMiddle,
  PageTitle,
} from '@supplement-bacon/react-toolkit/components'
import { SBTable, initialQuery } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import type { TableColumnsType } from 'antd'
import { Divider, Tooltip } from 'antd'
import { useEffect } from 'react'

const IntegrationsSevenroomsPage = () => {
  const dispatch = useAppDispatch()
  const { isOpen } = useSBRM()
  const audiences = useAppSelector(selectSevenroomsAudiences())
  const venueGroups = useAppSelector(selectSevenroomsVenueGroups())

  const { query, isLoading } = useAppSelector(
    (state) => state.sevenroomsAudience
  )

  const { query: venueGroupQuery, isLoading: venueGroupIsLoading } =
    useAppSelector((state) => state.sevenroomsVenueGroup)

  const columns: TableColumnsType<SevenroomsAudienceModel> = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Endpoint',
      render: (record: SevenroomsAudienceModel) => (
        <Tooltip title={record.endpoint}>
          <EllipsisMiddle
            suffixCount={10}
            style={{ maxWidth: 250 }}
            copyable={{ text: record.endpoint }}
          >
            {record.endpoint}
          </EllipsisMiddle>
        </Tooltip>
      ),
    },
    {
      title: 'Webhook integration',
      render: (record: SevenroomsAudienceModel) => (
        <BooleanTag value={record.webhookIntegration} />
      ),
    },
    {
      title: 'Venue group ID',
      render: (record: SevenroomsAudienceModel) => (
        <Tooltip title={record.venueGroupId}>
          <EllipsisMiddle
            suffixCount={10}
            style={{ maxWidth: 250 }}
            copyable={{ text: record.venueGroupId }}
          >
            {record.venueGroupId}
          </EllipsisMiddle>
        </Tooltip>
      ),
    },
    {
      title: 'Is global audience',
      render: (record: SevenroomsAudienceModel) => (
        <BooleanTag value={record.isGlobal} />
      ),
    },
    {
      title: 'Is default audience',
      render: (record: SevenroomsAudienceModel) => (
        <BooleanTag value={record.isDefault} />
      ),
    },
  ]

  const venueGroupColumns: TableColumnsType<SevenroomsVenueGroup> = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Venue group ID',
      render: (record: SevenroomsVenueGroup) => (
        <Tooltip title={record.venueGroupId}>
          <EllipsisMiddle
            style={{ maxWidth: 250 }}
            suffixCount={10}
            copyable={{ text: record.venueGroupId }}
          >
            {record.venueGroupId}
          </EllipsisMiddle>
        </Tooltip>
      ),
    },
    {
      title: 'Manager base URL',
      render: (record: SevenroomsVenueGroup) => (
        <Tooltip title={record.managerBaseUrl}>
          <EllipsisMiddle
            style={{ maxWidth: 250 }}
            suffixCount={10}
            copyable={{ text: record.managerBaseUrl }}
          >
            {record.managerBaseUrl}
          </EllipsisMiddle>
        </Tooltip>
      ),
    },
  ]

  useEffect(() => {
    if (isOpen) return
    dispatch(getSevenroomsAudiences(initialQuery))
    dispatch(getSevenroomsVenueGroups(initialQuery))
  }, [isOpen])

  return (
    <Page permissions={Permission.listAdmin}>
      <PageTitle
        title="Sevenrooms integration"
        level={4}
        style={{ margin: 0 }}
      />
      <Divider />
      <PageTitle
        title="Sevenrooms audiences"
        level={4}
        toolbar={
          <SBRM.Actions
            actions={[SBRMAction.Create]}
            entity={Entity.sevenroomsAudience}
          />
        }
      />
      <SBTable
        columns={columns}
        dataSource={audiences}
        loading={isLoading}
        pagination={query.pagination}
      />
      <PageTitle
        title="Sevenrooms venue groups"
        level={4}
        toolbar={
          <SBRM.Actions
            actions={[SBRMAction.Create]}
            entity={Entity.sevenroomsVenueGroup}
          />
        }
      />
      <SBTable
        columns={venueGroupColumns}
        dataSource={venueGroups}
        loading={venueGroupIsLoading}
        pagination={venueGroupQuery.pagination}
      />
    </Page>
  )
}

export default IntegrationsSevenroomsPage
